import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Check, ChevronDown, Shield, Download, Clock, Zap, Star, ArrowRight, BookOpen, Award, AlertTriangle, Users, Play, Pause, Volume2, VolumeX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Multi-language translations
const LANG = {
  es: { presents: "PRESENTA", problem: "EL PROBLEMA", losing: "Estas perdiendo", timeAndMoney: "tiempo y dinero.", solution: "LA SOLUCION", ready: "Todo listo.", copyPaste: "Solo copia y pega.", forYou: "Hecho para ti.", whatYouGet: "Esto es lo que obtienes", bonus: "BONUS", instant: "ACCESO INMEDIATO", stopSearch: "Deja de buscar.", startExec: "Empieza a ejecutar.", getNow: "OBTENER AHORA", securePay: "Pago seguro Wompi", instantAccess: "Acceso inmediato", lifetime: "De por vida", infomercial: "INFOMERCIAL", oneTime: "Pago unico — acceso de por vida", painQuote: "Cada hora buscando soluciones genericas es una hora que tu competencia usa para avanzar.", verified: "COMPRA VERIFICADA", success: "Compra exitosa", productReady: "Tu producto esta listo.", content: "Contenido" },
  en: { presents: "PRESENTS", problem: "THE PROBLEM", losing: "You are losing", timeAndMoney: "time and money.", solution: "THE SOLUTION", ready: "All set.", copyPaste: "Just copy and paste.", forYou: "Made for you.", whatYouGet: "This is what you get", bonus: "BONUS", instant: "INSTANT ACCESS", stopSearch: "Stop searching.", startExec: "Start executing.", getNow: "GET IT NOW", securePay: "Secure payment via Wompi", instantAccess: "Instant access", lifetime: "Lifetime access", infomercial: "INFOMERCIAL", oneTime: "One-time payment — lifetime access", painQuote: "Every hour searching for generic solutions is an hour your competition uses to get ahead.", verified: "PURCHASE VERIFIED", success: "Purchase successful", productReady: "Your product is ready.", content: "Content" },
  pt: { presents: "APRESENTA", problem: "O PROBLEMA", losing: "Voce esta perdendo", timeAndMoney: "tempo e dinheiro.", solution: "A SOLUCAO", ready: "Tudo pronto.", copyPaste: "Apenas copie e cole.", forYou: "Feito para voce.", whatYouGet: "Isso e o que voce recebe", bonus: "BONUS", instant: "ACESSO IMEDIATO", stopSearch: "Pare de procurar.", startExec: "Comece a executar.", getNow: "OBTER AGORA", securePay: "Pagamento seguro via Wompi", instantAccess: "Acesso imediato", lifetime: "Acesso vitalicio", infomercial: "INFOMERCIAL", oneTime: "Pagamento unico — acesso vitalicio", painQuote: "Cada hora buscando solucoes genericas e uma hora que sua concorrencia usa para avancar.", verified: "COMPRA VERIFICADA", success: "Compra realizada", productReady: "Seu produto esta pronto.", content: "Conteudo" },
  de: { presents: "PRASENTIERT", problem: "DAS PROBLEM", losing: "Sie verlieren", timeAndMoney: "Zeit und Geld.", solution: "DIE LOSUNG", ready: "Alles bereit.", copyPaste: "Einfach kopieren und einfugen.", forYou: "Fur Sie gemacht.", whatYouGet: "Das bekommen Sie", bonus: "BONUS", instant: "SOFORTIGER ZUGANG", stopSearch: "Horen Sie auf zu suchen.", startExec: "Fangen Sie an.", getNow: "JETZT HOLEN", securePay: "Sichere Zahlung uber Wompi", instantAccess: "Sofortiger Zugang", lifetime: "Lebenslanger Zugang", infomercial: "INFOMERCIAL", oneTime: "Einmalige Zahlung — lebenslanger Zugang", painQuote: "Jede Stunde mit generischen Losungen ist eine Stunde, die Ihre Konkurrenz nutzt.", verified: "KAUF BESTATIGT", success: "Kauf erfolgreich", productReady: "Ihr Produkt ist bereit.", content: "Inhalt" },
  fr: { presents: "PRESENTE", problem: "LE PROBLEME", losing: "Vous perdez", timeAndMoney: "du temps et de l'argent.", solution: "LA SOLUTION", ready: "Tout est pret.", copyPaste: "Copiez et collez.", forYou: "Fait pour vous.", whatYouGet: "Voici ce que vous obtenez", bonus: "BONUS", instant: "ACCES IMMEDIAT", stopSearch: "Arretez de chercher.", startExec: "Commencez a executer.", getNow: "OBTENIR MAINTENANT", securePay: "Paiement securise via Wompi", instantAccess: "Acces immediat", lifetime: "Acces a vie", infomercial: "INFOMERCIAL", oneTime: "Paiement unique — acces a vie", painQuote: "Chaque heure a chercher des solutions generiques est une heure que vos concurrents utilisent pour avancer.", verified: "ACHAT VERIFIE", success: "Achat reussi", productReady: "Votre produit est pret.", content: "Contenu" },
  ja: { presents: "PRESENTS", problem: "THE PROBLEM", losing: "You are losing", timeAndMoney: "time and money.", solution: "THE SOLUTION", ready: "All set.", copyPaste: "Just copy and paste.", forYou: "Made for you.", whatYouGet: "What you get", bonus: "BONUS", instant: "INSTANT ACCESS", stopSearch: "Stop searching.", startExec: "Start executing.", getNow: "GET IT NOW", securePay: "Secure payment", instantAccess: "Instant access", lifetime: "Lifetime", infomercial: "INFOMERCIAL", oneTime: "One-time — lifetime", painQuote: "Every hour searching is an hour your competition uses.", verified: "VERIFIED", success: "Purchase successful", productReady: "Your product is ready.", content: "Content" },
};

function detectLang() {
  const nav = navigator.language?.slice(0, 2) || "en";
  return LANG[nav] ? nav : "en";
}

function getT(productLang) {
  // Use product language first, fallback to browser, fallback to en
  if (LANG[productLang]) return LANG[productLang];
  const browser = detectLang();
  return LANG[browser] || LANG.en;
}

export function ProductPage() {
  const { productId } = useParams();
  const loc = useLocation();
  const isTY = loc.pathname.includes("/gracias");
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/product/${productId}`).then(r => setProduct(r.data)).catch(() => {}).finally(() => setLoading(false));
    // Fetch SEO data
    axios.get(`${API}/seo/product/${productId}`).then(r => setSeo(r.data?.seo || null)).catch(() => {});
  }, [productId]);

  // Inject SEO meta tags
  useEffect(() => {
    if (!product && !seo) return;
    const s = seo || {};
    const title = s.meta_title || product?.title || "GhostBrand";
    const desc = s.meta_description || product?.subtitle || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/product/${productId}`;
    const brand = product?.brand_name || "GhostBrand";
    const price = product?.price_usd || 19;
    const lang = product?.language || "en";

    // Title
    document.title = title;

    // Helper to set/create meta
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };

    // Standard meta
    setMeta("name", "description", desc);
    setMeta("name", "keywords", [s.primary_keyword, ...(s.secondary_keywords || [])].filter(Boolean).join(", "));
    setMeta("name", "author", brand);

    // Open Graph
    setMeta("property", "og:title", s.og_title || title);
    setMeta("property", "og:description", s.og_description || desc);
    setMeta("property", "og:url", s.canonical_url || url);
    setMeta("property", "og:type", "product");
    setMeta("property", "og:site_name", "GhostBrand");
    setMeta("property", "product:price:amount", String(price));
    setMeta("property", "product:price:currency", "USD");

    // Twitter Card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", s.twitter_title || title);
    setMeta("name", "twitter:description", s.twitter_description || desc);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", s.canonical_url || url);

    // Hreflang
    let hreflang = document.querySelector('link[rel="alternate"][hreflang]');
    if (!hreflang) { hreflang = document.createElement("link"); hreflang.setAttribute("rel", "alternate"); document.head.appendChild(hreflang); }
    hreflang.setAttribute("hreflang", lang);
    hreflang.setAttribute("href", s.canonical_url || url);

    // Schema.org JSON-LD
    let schema = document.querySelector('script[data-seo="product-schema"]');
    if (!schema) { schema = document.createElement("script"); schema.setAttribute("type", "application/ld+json"); schema.setAttribute("data-seo", "product-schema"); document.head.appendChild(schema); }
    const sp = s.schema_product || {};
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": sp.name || product?.title || "",
      "description": sp.description || desc,
      "brand": { "@type": "Brand", "name": sp.brand || brand },
      "category": sp.category || "Software",
      "offers": {
        "@type": "Offer",
        "price": price,
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": s.canonical_url || url,
      },
      "url": s.canonical_url || url,
    });

    // Cleanup on unmount
    return () => {
      document.title = "GhostBrand";
    };
  }, [product, seo, productId]);

  if (loading) return <div style={S.center}><div style={S.loader} /><style>{`@keyframes sp{to{transform:rotate(360deg)}}`}</style></div>;
  if (!product) return <div style={{...S.center, color: "#FF3B30", fontSize: 16}}>Producto no encontrado</div>;
  if (isTY) return <ThankYou product={product} />;
  return <Infomercial product={product} />;
}

/* ============ INFOMERCIAL PLAYER ============ */
function InfomercialPlayer({ product }) {
  const c = product.brand_color || "#007AFF";
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentSub, setCurrentSub] = useState("");
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const subtitles = product.subtitles || [];

  const togglePlay = () => {
    if (playing) {
      videoRef.current?.pause();
      audioRef.current?.pause();
      setPlaying(false);
    } else {
      videoRef.current?.play();
      audioRef.current?.play();
      setPlaying(true);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) audioRef.current.muted = !muted;
    setMuted(!muted);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const update = () => {
      const t = audio.currentTime;
      const d = audio.duration || 1;
      setProgress((t / d) * 100);
      let sub = "";
      for (let i = subtitles.length - 1; i >= 0; i--) {
        if (t >= subtitles[i].t) { sub = subtitles[i].text; break; }
      }
      setCurrentSub(sub);
    };
    audio.addEventListener("timeupdate", update);
    audio.addEventListener("ended", () => { setPlaying(false); setCurrentSub(""); setProgress(0); });
    return () => { audio.removeEventListener("timeupdate", update); };
  }, [subtitles]);

  return (
    <div data-testid="infomercial-player" style={{
      position: "relative", width: "100%", maxWidth: 900, margin: "0 auto",
      borderRadius: 12, overflow: "hidden", background: "#000",
      border: `1px solid ${c}20`, boxShadow: `0 0 60px ${c}15`
    }}>
      {/* Video */}
      <video ref={videoRef} muted loop playsInline style={{ width: "100%", display: "block", aspectRatio: "16/9" }}>
        {product.video_url && <source src={product.video_url} type="video/mp4" />}
      </video>
      {/* Audio (hidden) */}
      <audio ref={audioRef} preload="auto">
        {product.audio_url && <source src={product.audio_url} type="audio/mpeg" />}
      </audio>

      {/* Subtitle overlay */}
      <div style={{
        position: "absolute", bottom: 60, left: 0, right: 0,
        display: "flex", justifyContent: "center", padding: "0 20px", pointerEvents: "none"
      }}>
        {currentSub && (
          <div style={{
            padding: "10px 24px", background: "rgba(0,0,0,0.85)", borderRadius: 6,
            fontFamily: "'Chivo'", fontSize: "clamp(14px, 2.5vw, 22px)", fontWeight: 600,
            color: "#fff", textAlign: "center", maxWidth: 700, lineHeight: 1.4,
            animation: "subFade 0.3s ease", textShadow: "0 2px 8px rgba(0,0,0,0.5)"
          }}>
            {currentSub}
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "linear-gradient(transparent, rgba(0,0,0,0.9))", padding: "24px 16px 12px"
      }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: "rgba(255,255,255,0.15)", borderRadius: 2, marginBottom: 10, cursor: "pointer" }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            if (audioRef.current) { audioRef.current.currentTime = pct * audioRef.current.duration; }
            if (videoRef.current) { videoRef.current.currentTime = pct * (videoRef.current.duration || 12); }
          }}>
          <div style={{ height: "100%", width: `${progress}%`, background: c, borderRadius: 2, transition: "width 0.2s" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={togglePlay} data-testid="play-btn" style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}>
            {playing ? <Pause size={20} fill="#fff" style={{ color: "#fff" }} /> : <Play size={20} fill="#fff" style={{ color: "#fff" }} />}
          </button>
          <button onClick={toggleMute} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}>
            {muted ? <VolumeX size={16} style={{ color: "#71717a" }} /> : <Volume2 size={16} style={{ color: "#fff" }} />}
          </button>
          <span style={{ fontFamily: "'IBM Plex Mono'", fontSize: 10, color: "#71717a", marginLeft: "auto" }}>INFOMERCIAL</span>
        </div>
      </div>

      {/* Play overlay (when paused) */}
      {!playing && (
        <div onClick={togglePlay} style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.5)", cursor: "pointer", zIndex: 5
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%", background: `${c}cc`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 0 40px ${c}50`, transition: "transform 0.2s"
          }}>
            <Play size={32} fill="#fff" style={{ color: "#fff", marginLeft: 4 }} />
          </div>
        </div>
      )}

      <style>{`@keyframes subFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}


/* ============ MAIN INFOMERCIAL PAGE ============ */
function Infomercial({ product }) {
  const c = product.brand_color || "#007AFF";
  const chapters = product.chapters || [];
  const deliverables = product.key_deliverables || [];
  const [vis, setVis] = useState({});
  const refs = useRef({});
  const t = getT(product.language);
  const price = product.price_usd || 19;
  const trackedScrolls = useRef({});

  // Event tracking
  const track = (event, meta) => {
    axios.post(`${API}/track`, { product_id: product.id, event, metadata: meta || {} }).catch(() => {});
  };

  // Track page view
  useEffect(() => { track("page_view"); }, []);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (h <= 0) return;
      const pct = Math.round((window.scrollY / h) * 100);
      [25, 50, 75, 100].forEach(threshold => {
        if (pct >= threshold && !trackedScrolls.current[threshold]) {
          trackedScrolls.current[threshold] = true;
          track(`scroll_${threshold}`);
        }
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track CTA clicks
  const trackCheckout = () => track("checkout_click", { price_usd: price });

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setVis(v => ({...v, [e.target.id]: true})); });
    }, { threshold: 0.15 });
    Object.values(refs.current).forEach(el => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const setRef = (id) => (el) => { if (el) refs.current[id] = el; };
  const isV = (id) => vis[id];

  return (
    <div data-testid="product-page" style={{ background: "#000", color: "#fff", fontFamily: "'IBM Plex Sans',-apple-system,sans-serif" }}>
      {/* Nav */}
      <nav style={{ position: "sticky", top: 0, zIndex: 50, padding: "8px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(0,0,0,0.85)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
          <span style={{ fontFamily: "'Chivo'", fontWeight: 700, fontSize: 14 }}>{product.brand_name}</span>
        </div>
        <a href={product.checkout_url} onClick={trackCheckout} data-testid="nav-buy-btn" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 20px", background: c, color: "#fff", textDecoration: "none", fontFamily: "'IBM Plex Mono'", fontSize: 11, fontWeight: 700, borderRadius: 4 }}>
          <ShoppingCart size={13} /> ${price} USD
        </a>
      </nav>

      {/* ===== HERO: VIDEO INFOMERCIAL ===== */}
      <section style={{ padding: "48px 24px 32px", textAlign: "center" }}>
        <div style={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, color: c, letterSpacing: "0.25em", marginBottom: 12 }}>
          {product.brand_name?.toUpperCase()} {t.presents}
        </div>
        <h1 style={{ fontFamily: "'Chivo'", fontWeight: 800, fontSize: "clamp(28px,5vw,48px)", lineHeight: 1.05, letterSpacing: "-0.03em", maxWidth: 800, margin: "0 auto 16px" }}>
          {product.title}
        </h1>
        <p style={{ fontSize: 17, color: "#a1a1aa", lineHeight: 1.6, maxWidth: 600, margin: "0 auto 32px" }}>
          {product.subtitle}
        </p>

        <InfomercialPlayer product={product} />

        <div style={{ marginTop: 32 }}>
          <a href={product.checkout_url} onClick={trackCheckout} data-testid="buy-button" style={{
            display: "inline-flex", alignItems: "center", gap: 10, padding: "18px 44px",
            background: `linear-gradient(135deg, ${c}, ${c}cc)`, color: "#fff", textDecoration: "none",
            fontFamily: "'IBM Plex Mono'", fontSize: 16, fontWeight: 700, letterSpacing: "0.08em",
            borderRadius: 4, boxShadow: `0 8px 40px ${c}40`
          }}>
            <Zap size={20} strokeWidth={2.5} /> {t.getNow} — ${price} USD
          </a>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16 }}>
            {[{i:Shield,txt:t.securePay},{i:Download,txt:t.instantAccess},{i:Clock,txt:t.lifetime}].map((x,i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <x.i size={12} style={{ color: "#3f3f46" }} />
                <span style={{ fontFamily: "'IBM Plex Mono'", fontSize: 9, color: "#52525b" }}>{x.txt}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROBLEM ===== */}
      <section id="prob" ref={setRef("prob")} style={{ padding: "64px 24px", background: "#050505" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", opacity: isV("prob") ? 1 : 0, transform: isV("prob") ? "none" : "translateY(30px)", transition: "all 0.8s ease" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <AlertTriangle size={18} style={{ color: "#FF3B30" }} />
            <span style={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, color: "#FF3B30", letterSpacing: "0.2em", fontWeight: 700 }}>{t.problem}</span>
          </div>
          <h2 style={{ fontFamily: "'Chivo'", fontSize: 32, fontWeight: 800, lineHeight: 1.1, marginBottom: 16 }}>
            {t.losing} <span style={{ color: "#FF3B30" }}>{t.timeAndMoney}</span>
          </h2>
          <p style={{ fontSize: 17, color: "#a1a1aa", lineHeight: 1.75, marginBottom: 16 }}>{product.pain_point}</p>
          <div style={{ padding: "16px 20px", borderLeft: "3px solid #FF3B30", background: "rgba(255,59,48,0.04)" }}>
            <p style={{ fontSize: 14, color: "#d4d4d8", lineHeight: 1.6, fontStyle: "italic" }}>"{t.painQuote}"</p>
          </div>
        </div>
      </section>

      {/* ===== SOLUTION ===== */}
      <section id="sol" ref={setRef("sol")} style={{ padding: "64px 24px", background: `radial-gradient(ellipse at 50% 50%, ${c}06, transparent 60%), #000` }}>
        <div style={{ maxWidth: 800, margin: "0 auto", opacity: isV("sol") ? 1 : 0, transform: isV("sol") ? "none" : "translateY(30px)", transition: "all 0.8s ease" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <Zap size={18} style={{ color: c }} />
            <span style={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, color: c, letterSpacing: "0.2em", fontWeight: 700 }}>{t.solution}</span>
          </div>
          <h2 style={{ fontFamily: "'Chivo'", fontSize: 32, fontWeight: 800, lineHeight: 1.1, marginBottom: 16 }}>
            {t.ready} <span style={{ color: c }}>{t.copyPaste}</span>
          </h2>
          <p style={{ fontSize: 17, color: "#a1a1aa", lineHeight: 1.75, marginBottom: 24 }}>{product.unique_value || product.subtitle}</p>
          {deliverables.slice(0, 4).map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0" }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: `${c}12`, border: `1px solid ${c}25`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Check size={13} strokeWidth={3} style={{ color: c }} />
              </div>
              <span style={{ fontSize: 15, color: "#d4d4d8", lineHeight: 1.5 }}>{d}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== WHO ===== */}
      <section id="who" ref={setRef("who")} style={{ padding: "64px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 700, margin: "0 auto", opacity: isV("who") ? 1 : 0, transform: isV("who") ? "none" : "translateY(20px)", transition: "all 0.6s ease" }}>
          <Users size={24} style={{ color: c, marginBottom: 12 }} />
          <h2 style={{ fontFamily: "'Chivo'", fontSize: 32, fontWeight: 800, marginBottom: 20 }}>{t.forYou}</h2>
          <p style={{ fontSize: 18, color: "#d4d4d8", lineHeight: 1.8, padding: "24px 28px", background: `${c}04`, border: `1px solid ${c}10`, borderRadius: 8 }}>
            {product.target_audience}
          </p>
        </div>
      </section>

      {/* ===== CONTENT ===== */}
      <section id="cnt" ref={setRef("cnt")} style={{ padding: "64px 24px", background: "#050505" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <BookOpen size={24} style={{ color: c, marginBottom: 8 }} />
            <h2 style={{ fontFamily: "'Chivo'", fontSize: 32, fontWeight: 800 }}>{t.whatYouGet}</h2>
          </div>
          {chapters.map((ch, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", marginBottom: 6,
              background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 6,
              opacity: isV("cnt") ? 1 : 0, transform: isV("cnt") ? "none" : "translateX(-20px)",
              transition: `all 0.5s ease ${i * 0.08}s`
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 6, background: `${c}10`, border: `1px solid ${c}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontFamily: "'Chivo'", fontSize: 14, fontWeight: 800, color: c }}>{String(i+1).padStart(2,"0")}</span>
              </div>
              <span style={{ fontSize: 15, color: "#d4d4d8", flex: 1 }}>{ch}</span>
            </div>
          ))}
          {deliverables.length > 0 && (
            <div style={{ marginTop: 20, padding: "20px 24px", background: "rgba(52,199,89,0.04)", border: "1px solid rgba(52,199,89,0.1)", borderRadius: 8 }}>
              <div style={{ fontFamily: "'IBM Plex Mono'", fontSize: 10, color: "#34C759", letterSpacing: "0.15em", marginBottom: 12, fontWeight: 700 }}>{t.bonus}</div>
              {deliverables.map((d, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0" }}>
                  <Check size={14} strokeWidth={3} style={{ color: "#34C759", flexShrink: 0, marginTop: 3 }} />
                  <span style={{ fontSize: 13, color: "#d4d4d8" }}>{d}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section style={{ padding: "80px 24px", textAlign: "center", background: `radial-gradient(ellipse at 50% 60%, ${c}10, transparent 50%), #000` }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          {/* Social proof */}
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 20 }}>
            {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="#facc15" style={{ color: "#facc15" }} />)}
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono'", fontSize: 10, color: "#52525b", letterSpacing: "0.2em", marginBottom: 12 }}>{t.instant}</div>
          <h2 style={{ fontFamily: "'Chivo'", fontSize: 36, fontWeight: 800, lineHeight: 1.1, marginBottom: 16 }}>
            {t.stopSearch}<br/><span style={{ color: c }}>{t.startExec}</span>
          </h2>
          {/* Price anchor */}
          <div style={{ marginBottom: 20 }}>
            <span style={{ fontFamily: "'IBM Plex Mono'", fontSize: 13, color: "#52525b", textDecoration: "line-through" }}>${Math.round(price * 3)} USD</span>
            <span style={{ fontFamily: "'Chivo'", fontSize: 32, fontWeight: 800, color: "#fff", marginLeft: 12 }}>${price} USD</span>
          </div>
          <a href={product.checkout_url} onClick={trackCheckout} data-testid="buy-button-bottom" style={{
            display: "inline-flex", alignItems: "center", gap: 10, padding: "20px 56px",
            background: `linear-gradient(135deg, ${c}, ${c}cc)`, color: "#fff", textDecoration: "none",
            fontFamily: "'IBM Plex Mono'", fontSize: 17, fontWeight: 700, letterSpacing: "0.08em",
            borderRadius: 4, boxShadow: `0 8px 48px ${c}50`,
            animation: "ctaPulse 2s ease-in-out infinite",
          }}>
            <Zap size={20} strokeWidth={2.5} /> {t.getNow} — ${price} USD
          </a>
          <div style={{ marginTop: 12, fontFamily: "'IBM Plex Mono'", fontSize: 10, color: "#facc15", letterSpacing: "0.08em" }}>
            {t.oneTime}
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
            <Shield size={12} style={{ color: "#3f3f46" }} />
            <span style={{ fontFamily: "'IBM Plex Mono'", fontSize: 9, color: "#3f3f46" }}>{t.securePay}</span>
          </div>
        </div>
      </section>
      <style>{`@keyframes ctaPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.02)}}`}</style>
    </div>
  );
}


/* ============ THANK YOU ============ */
function ThankYou({ product }) {
  const [content, setContent] = useState(null);
  const c = product.brand_color || "#007AFF";
  const t = getT(product.language);
  useEffect(() => { axios.get(`${API}/product/${product.id}/download/${product.download_token}`).then(r => setContent(r.data)).catch(() => {}); }, [product]);

  return (
    <div data-testid="thank-you-page" style={{ background: "#000", color: "#fff", fontFamily: "'IBM Plex Sans',sans-serif", minHeight: "100vh" }}>
      <div style={{ borderBottom: `2px solid ${c}`, padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: c }} /><span style={{ fontFamily: "'Chivo'", fontWeight: 700, fontSize: 14 }}>{product.brand_name}</span></div>
        <span style={{ fontFamily: "'IBM Plex Mono'", fontSize: 9, color: "#34C759", letterSpacing: "0.1em" }}>{t.verified}</span>
      </div>
      <div style={{ background: `radial-gradient(ellipse at 50% 0%, ${c}12, transparent 50%)`, padding: "48px 24px", textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: `${c}15`, border: `2px solid ${c}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}><Check size={28} style={{ color: c }} /></div>
        <h1 style={{ fontFamily: "'Chivo'", fontSize: 28, fontWeight: 800, marginBottom: 8 }}>{t.success}</h1>
        <p style={{ color: "#a1a1aa", fontSize: 15 }}>{t.productReady}</p>
      </div>
      {content && (
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 60px" }}>
          <div style={{ padding: "36px", margin: "28px 0", background: `${c}04`, border: `1px solid ${c}12`, borderRadius: 10, textAlign: "center" }}>
            <h2 style={{ fontFamily: "'Chivo'", fontSize: 24, fontWeight: 800, marginBottom: 8 }}>{content.title}</h2>
            <p style={{ fontSize: 13, color: "#a1a1aa" }}>{content.subtitle}</p>
            <div style={{ fontFamily: "'IBM Plex Mono'", fontSize: 10, color: "#52525b", marginTop: 8 }}>{content.author}</div>
          </div>
          {content.sections?.map((s, i) => (
            <div key={i} style={{ padding: "24px 0", borderTop: i > 0 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, color: c, fontWeight: 700, padding: "3px 8px", background: `${c}10`, borderRadius: 4 }}>{String(i+1).padStart(2,"0")}</span>
                <h3 style={{ fontFamily: "'Chivo'", fontSize: 20, fontWeight: 700 }}>{s.title}</h3>
              </div>
              <div className="pbody" style={{ fontSize: 15, lineHeight: 1.85, color: "#d4d4d8", paddingLeft: 16, borderLeft: `2px solid ${c}10` }} dangerouslySetInnerHTML={{ __html: s.content }} />
            </div>
          ))}
          {content.bonus_checklist?.length > 0 && (
            <div style={{ padding: "20px", margin: "20px 0", background: "rgba(52,199,89,0.04)", border: "1px solid rgba(52,199,89,0.1)", borderRadius: 8 }}>
              <div style={{ fontFamily: "'Chivo'", fontSize: 15, fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><Award size={14} style={{ color: "#34C759" }} /> Bonus</div>
              {content.bonus_checklist.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0" }}><Check size={13} strokeWidth={3} style={{ color: "#34C759" }} /><span style={{ fontSize: 13, color: "#d4d4d8" }}>{item}</span></div>
              ))}
            </div>
          )}
        </div>
      )}
      <style>{`.pbody h3{font-family:'Chivo',sans-serif;font-size:16px;font-weight:600;color:#fff;margin:18px 0 8px}.pbody p{margin:0 0 10px}.pbody ul{padding-left:18px;margin:8px 0 10px}.pbody li{margin:4px 0;color:#a1a1aa}.pbody li::marker{color:#007AFF}.pbody strong{color:#fff;font-weight:600}`}</style>
    </div>
  );
}

const S = {
  center: { minHeight: "100vh", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'IBM Plex Mono'" },
  loader: { width: 28, height: 28, border: "2px solid rgba(255,255,255,0.1)", borderTopColor: "#007AFF", borderRadius: "50%", animation: "sp 0.6s linear infinite" },
};
